import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { IAccountAppealPreSubmissionProps } from "../../model/interface";
import { Dropdown, Input, Label, Option, Textarea } from "@fluentui/react-components";
import { EMAIL_MAX_LENGTH, JUSTIFICATION_MAX_LENGTH, NAME_MAX_LENGTH } from "../../constants/constants";
import { getProductList } from "../../utils/appealUtil";
import Required from "../required/required";
import './AccountAppealPreSubmission.scss';
import { isEmailValid } from "../../utils/formValidator";

export default function AccountAppealPreSubmission(props: IAccountAppealPreSubmissionProps) {
    const appealsProducts = getProductList();
    const initialProduct = (props.currentFormInfo.product !== "" && props.currentFormInfo.product !== null) ? appealsProducts[props.currentFormInfo.product] : "";
    const showProductField = !props.currentFormInfo.appealNumber && props.currentFormInfo.shouldAskForProduct;
    const [product, setProduct] = useState<string>(initialProduct);
    const [name, setName] = useState<string>("");
    const [contactEmail, setContactEmail] = useState<string>("");
    const [justification, setJustification] = useState<string>("");
    const [hasEditedName, setHasEditedName] = useState<boolean>(false);
    const [hasEditedContactEmail, setHasEditedContactEmail] = useState<boolean>(false);
    const [hasEditedJustification, setHasEditedJustification] = useState<boolean>(false);
    const [contactEmailError, setContactEmailError] = useState<string | null>(null);
    const { t } = useTranslation();
    const displayedJustification = hasEditedJustification ? justification : props.currentFormInfo.appealerJustification;
    const displayedName = hasEditedName ? name : props.currentFormInfo.appealerName;
    const displayedContactEmail = hasEditedContactEmail ? contactEmail : props.currentFormInfo.contactEmail;

    const handleProductSelect = (product: string) => {
        const newProduct = product;
        setProduct(appealsProducts[newProduct]);
        props.productChange(newProduct);
    }

    const handleNameChange = (name: string) => {
        const newName = name;
        setHasEditedName(true);
        setName(newName);
        props.nameChange(newName);
    }

    const handleContactEmailChange = (contactEmail: string) => {
        const newContactEmail = contactEmail;
        setHasEditedContactEmail(true);
        setContactEmail(newContactEmail);
        props.contactEmailChange(newContactEmail);
    }

    const handleJustificationChange = (justification: string) => {
        const newJustificaiton = justification;
        setHasEditedJustification(true);
        setJustification(newJustificaiton);
        props.justificationChange(newJustificaiton);
    }

    const validateEmail = (email: string) => {
        if (!email) {
            setContactEmailError(null);
            return;
        }
        if (!isEmailValid(email)) {
            setContactEmailError(t("Appeal.Common.email-invalid"));
        } else {
            setContactEmailError(null);
        }
    };

    const dropDownOptions = (): ReactElement[] => {
        return Object.entries(appealsProducts).map(([key, value]) => {
            return (
                <Option
                    value={key}
                    data-testid={key.toLocaleLowerCase()}
                    key={key}
                >
                    {value}
                </Option>
            );
        });
    };

    return (
        <div>
            <div className="form-overview">
                <h1 className="form-overview__header"> {t('Appeal.Common.submit-an-appeal')}</h1>
                <p className="form-overview__paragraph">{t('Appeal.Common.pre-submission-overview')}</p>
            </div>
            <div className="form-fields">
                {showProductField &&
                    <div className="product">
                        <Label id="productLabel">
                            {t('Appeal.Account.service-or-product')}
                            <span><Required /></span>
                        </Label>
                        <Dropdown
                            id="appealerProduct"
                            aria-labelledby="productLabel"
                            data-testid="appealerProduct"
                            placeholder={product ? undefined : t('Appeal.Common.service-or-product-placeholder')}
                            onOptionSelect={(e, data) => handleProductSelect(String(data.optionValue))}
                            selectedOptions={props.currentFormInfo.product ? [props.currentFormInfo.product] : []}
                            value={product}
                            autoFocus={showProductField}
                        >
                            {dropDownOptions()}
                        </Dropdown>
                    </div>
                }
                <div className="description">
                    <Label id="descriptionLabel">
                        {t('Appeal.Common.description')}
                        <span><Required /></span>
                    </Label>
                    <Textarea
                        id="appealerJustification"
                        aria-labelledby="descriptionLabel"
                        className="public-forms-justification"
                        data-testid="appealerJustification"
                        required
                        onChange={(e: any, data: { value: string; }) => handleJustificationChange(data.value)}
                        placeholder={displayedJustification ? undefined : t('Appeal.Account.description-placeholder')}
                        value={displayedJustification}
                        maxLength={JUSTIFICATION_MAX_LENGTH}
                        autoFocus={!showProductField}
                    />
                </div>
                <div className="name">
                    <Label id="nameLabel">
                        {t('Appeal.Common.name')}
                        <span><Required /></span>
                    </Label>
                    <Input
                        id="appealerName"
                        aria-labelledby="nameLabel"
                        data-testid="appealerName"
                        type="text"
                        onChange={(e: any, data: { value: string; }) => handleNameChange(data.value)}
                        placeholder={displayedName ? undefined : t('Appeal.Account.full-name-placeholder')}
                        value={displayedName}
                        maxLength={NAME_MAX_LENGTH}
                    />
                </div>
                <div className="email">
                    <Label id="contactEmailLabel">
                        {t('Appeal.Account.contact-email-address')}
                        <span><Required /></span>
                    </Label>
                    <Input
                        id="contactEmail"
                        className="emailInput"
                        aria-labelledby="contactEmailLabel"
                        data-testid="contactEmail"
                        type="email"
                        required
                        onChange={(e: any, data: { value: string; }) => handleContactEmailChange(data.value)}
                        onBlur={() => validateEmail(displayedContactEmail)}
                        placeholder={displayedContactEmail ? undefined : t('Appeal.Account.contact-email-address-placeholder')}
                        value={displayedContactEmail}
                        maxLength={EMAIL_MAX_LENGTH}
                    />
                    {contactEmailError && (
                        <span className="error-message" role="alert" aria-live="assertive">{contactEmailError}</span>
                    )}
                    <span className="subtext__presubmission">{t('Appeal.Account.contact-email-address-subtext')}</span>
                </div>

            </div>
        </div>
    );
};